import {
  collection,
  doc,
  getDocs,
  setDoc,
  deleteDoc,
  onSnapshot,
  query,
  orderBy,
  writeBatch,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { toNumber } from '../utils/calc';
import { toIsoDate } from '../utils/format';

const PERFORMANCE_COLLECTION = 'performance';

/**
 * Monthly fund performance series. Each doc is keyed by its ISO date so
 * importing the same month twice updates rather than duplicates.
 * Shape: { date: 'YYYY-MM-DD', value: number, contributedCapital: number }
 */
export const performanceService = {
  /** Subscribe to the full performance series, ordered by date. */
  subscribeToPerformance: (callback, errorCallback) => {
    const q = query(collection(db, PERFORMANCE_COLLECTION), orderBy('date', 'asc'));
    return onSnapshot(
      q,
      (snapshot) => callback(snapshot.docs.map((d) => ({ id: d.id, ...d.data() }))),
      errorCallback
    );
  },

  /** One-time read of the full performance series. */
  getAll: async () => {
    const q = query(collection(db, PERFORMANCE_COLLECTION), orderBy('date', 'asc'));
    const snapshot = await getDocs(q);
    return snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
  },

  /** Create or update a single month (keyed by ISO date). */
  upsertPoint: async ({ date, value, contributedCapital }) => {
    const iso = toIsoDate(date);
    await setDoc(doc(db, PERFORMANCE_COLLECTION, iso), {
      date: iso,
      value: toNumber(value) || 0,
      contributedCapital: toNumber(contributedCapital) || 0,
      updatedAt: serverTimestamp(),
    });
  },

  /** Delete a performance point by its document id. */
  deletePoint: async (id) => {
    await deleteDoc(doc(db, PERFORMANCE_COLLECTION, id));
  },

  /**
   * Bulk import an array of { date, value, contributedCapital } rows.
   * Rows are keyed by ISO date so re-importing is idempotent.
   */
  importPoints: async (rows) => {
    const batch = writeBatch(db);
    let count = 0;
    rows.forEach((row) => {
      const iso = toIsoDate(row.date);
      if (!iso) return;
      batch.set(doc(db, PERFORMANCE_COLLECTION, iso), {
        date: iso,
        value: toNumber(row.value) || 0,
        contributedCapital: toNumber(row.contributedCapital) || 0,
        updatedAt: serverTimestamp(),
      });
      count += 1;
    });
    await batch.commit();
    return count;
  },
};
